import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  Key, 
  Link2, 
  FolderOpen,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Bell,
  Lock,
  Calendar,
  FileQuestion,
  Trash2,
  UserPlus,
  Eye,
  Search,
  Shield,
  ChevronDown,
  Loader2,
  RotateCw,
  UserX,
  Undo2,
  Wrench,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function SettingsPage() {
  const { token, changePassword } = useAuth();
  const [settings, setSettings] = useState({
    campminder_subscription_key: "",
    google_drive_folder_id: ""
  });
  const [seasonId, setSeasonId] = useState("2026");
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [triggeringEmails, setTriggeringEmails] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [sendingTestSms, setSendingTestSms] = useState(false);

  // Unmatched files state
  const [unmatchedFiles, setUnmatchedFiles] = useState([]);
  const [loadingUnmatched, setLoadingUnmatched] = useState(false);
  const [allStaff, setAllStaff] = useState([]);
  const [staffSearch, setStaffSearch] = useState({});
  const [selectedStaff, setSelectedStaff] = useState({});
  const [selectedCertType, setSelectedCertType] = useState({});
  const [assigningFile, setAssigningFile] = useState(null);
  const [deletingFile, setDeletingFile] = useState(null);
  const [staffDropdownOpen, setStaffDropdownOpen] = useState({});
  const [thumbnails, setThumbnails] = useState({});

  // Camp season dates
  const [campStart, setCampStart] = useState("2026-06-29");
  const [campEnd, setCampEnd] = useState("2026-08-20");
  const [savingCampDates, setSavingCampDates] = useState(false);

  // Backfill cross-cert links
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [runningBackfill, setRunningBackfill] = useState(false);
  const [applyingBackfill, setApplyingBackfill] = useState(false);
  // Auto-extract dates background job
  const [extractJob, setExtractJob] = useState(null);
  const [pollingJob, setPollingJob] = useState(false);
  const [loadingThumbnails, setLoadingThumbnails] = useState({});
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [unmatchedPage, setUnmatchedPage] = useState(0);
  const [manualDates, setManualDates] = useState({});
  const [imageRotation, setImageRotation] = useState({});
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [notHiredFiles, setNotHiredFiles] = useState([]);
  const [showNotHired, setShowNotHired] = useState(false);
  const [loadingNotHired, setLoadingNotHired] = useState(false);
  const [movingToNotHired, setMovingToNotHired] = useState(null);
  const [restoringFile, setRestoringFile] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const CERT_TYPES = [
    { id: "cpr_aed", name: "CPR/AED" },
    { id: "cpr_aed_rte_combo", name: "AED/CPR/RTE Combo" },
    { id: "cpr_aed_instructor", name: "CPR/AED Instructor" },
    { id: "lifeguard", name: "Lifeguard" },
    { id: "wsi", name: "Water Safety Instructor (WSI)" },
    { id: "health_nursing", name: "Health/Nursing" },
    { id: "boating", name: "Boating" },
    { id: "ceu", name: "CEU/CPR" },
    { id: "epipen", name: "Epi Pen/Anaphylaxis" },
    { id: "bls", name: "Basic Life Support" },
    { id: "pal", name: "Pool Activity Leader" },
  ];

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        campminder_subscription_key: response.data.campminder_configured ? "••••••••" : "",
        google_drive_folder_id: response.data.google_drive_folder_id || ""
      });
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchCampDates = async () => {
    try {
      const resp = await axios.get(`${API}/settings/camp-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampStart(resp.data.camp_start);
      setCampEnd(resp.data.camp_end);
    } catch {
      // keep defaults
    }
  };

  const handleSaveCampDates = async () => {
    if (!campStart || !campEnd) {
      toast.error("Both camp start and end dates are required");
      return;
    }
    if (new Date(campEnd) < new Date(campStart)) {
      toast.error("Camp end date must be on or after camp start date");
      return;
    }
    setSavingCampDates(true);
    try {
      await axios.put(`${API}/settings/camp-dates`,
        { camp_start: campStart, camp_end: campEnd },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Camp season dates saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save camp dates");
    } finally {
      setSavingCampDates(false);
    }
  };

  const handleRunBackfillPreview = async () => {
    setRunningBackfill(true);
    try {
      const resp = await axios.post(
        `${API}/admin/backfill-certifications?dry_run=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackfillPreview(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill preview failed");
    } finally {
      setRunningBackfill(false);
    }
  };

  const handleApplyBackfill = async () => {
    setApplyingBackfill(true);
    try {
      const resp = await axios.post(
        `${API}/admin/backfill-certifications?dry_run=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Backfill applied: ${resp.data.combo_extended_cpr} combo→CPR, ${resp.data.ceu_extended_cpr} CEU→CPR. ${resp.data.staff_with_missing_dates} staff still need expiration dates.`
      );
      setBackfillPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill apply failed");
    } finally {
      setApplyingBackfill(false);
    }
  };

  const handleAutoExtractDates = async () => {
    if (!window.confirm("Run AI date extraction on all certs with missing expiration dates? This uses Gemini Vision and may take a few minutes. Cost is roughly $0.005 per cert.")) {
      return;
    }
    setPollingJob(true);
    setExtractJob({ status: "starting", progress: {} });
    try {
      const start = await axios.post(
        `${API}/admin/backfill-certifications?extract_dates=true&dry_run=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jobId = start.data.job_id;
      setExtractJob({ status: "running", job_id: jobId, progress: {} });

      // Poll every 5s until complete
      const poll = async () => {
        const resp = await axios.get(
          `${API}/admin/backfill-certifications/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setExtractJob(resp.data);
        if (resp.data.status === "complete" || resp.data.status === "error") {
          setPollingJob(false);
          if (resp.data.status === "complete") {
            const sm = resp.data.summary || {};
            toast.success(
              `Extracted dates for ${sm.dates_auto_extracted} certs. ${sm.low_confidence_extractions} low-confidence, ${sm.needs_manual_review} need manual review.`
            );
          } else {
            toast.error("Extraction job failed: " + (resp.data.error || "unknown"));
          }
          return;
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setPollingJob(false);
      setExtractJob(null);
      toast.error(e.response?.data?.detail || "Failed to start extraction job");
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchCampDates();
    fetchUnmatchedFiles();
    fetchAllStaff();
    fetchNotHiredFiles();
    fetchNotificationLogs();
  }, [token]);

  // Close staff dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setStaffDropdownOpen({});
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchUnmatchedFiles = async () => {
    setLoadingUnmatched(true);
    try {
      const response = await axios.get(`${API}/unmatched-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnmatchedFiles(response.data);
      // Initialize cert type selections and manual dates from detected values
      const certTypes = {};
      const dates = {};
      response.data.forEach(f => {
        certTypes[f.id] = f.detected_cert_type || "cpr_aed";
        dates[f.id] = {
          date_completed: f.date_completed ? f.date_completed.split("T")[0] : "",
          expiration_date: f.expiration_date ? f.expiration_date.split("T")[0] : ""
        };
      });
      setSelectedCertType(prev => ({ ...prev, ...certTypes }));
      setManualDates(prev => ({ ...prev, ...dates }));
    } catch {
      // Silently handle - may not have any unmatched files
    } finally {
      setLoadingUnmatched(false);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff/all-names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllStaff(response.data);
    } catch {
      // Fallback: try regular staff endpoint
      try {
        const response = await axios.get(`${API}/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllStaff(response.data);
      } catch {}
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (settings.campminder_subscription_key && !settings.campminder_subscription_key.includes("•")) {
        payload.campminder_subscription_key = settings.campminder_subscription_key;
      }
      payload.google_drive_folder_id = settings.google_drive_folder_id;
      
      await axios.post(`${API}/settings/update`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const fetchNotificationLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(`${API}/notification-log?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationLogs(response.data);
    } catch {
      // silently handle
    } finally {
      setLoadingLogs(false);
    }
  };

  const refreshDeliveryStatus = async () => {
    setRefreshingStatus(true);
    try {
      const response = await axios.post(`${API}/notification-log/refresh-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Updated ${response.data.updated} delivery statuses`);
      fetchNotificationLogs();
    } catch {
      toast.error("Failed to refresh delivery status");
    } finally {
      setRefreshingStatus(false);
    }
  };

  const testCampMinderConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await axios.get(`${API}/campminder/test-connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data);
    } catch (error) {
      setConnectionStatus({ connected: false, error: "Connection test failed" });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSyncCampMinder = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/campminder/sync?season_id=${seasonId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Sync started in background");
      // Poll for sync status
      pollSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const [repairing, setRepairing] = useState(false);
  const handleRepairCategories = async () => {
    if (!window.confirm("Recompute staff_category for ALL active staff based on their current division + position. Use this if any division is missing from the front-end. Continue?")) return;
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/admin/repair-staff-categories`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { categories_repaired, total_active_staff, still_uncategorized_other, reactivated, division_names_fixed } = response.data;
      toast.success(`Repair: ${reactivated || 0} reactivated, ${division_names_fixed || 0} division names fixed, ${categories_repaired} recategorized (of ${total_active_staff} total).`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const pollSyncStatus = () => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/campminder/sync-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { status, last_sync_count, error } = response.data;
        if (status === "complete") {
          clearInterval(interval);
          setSyncing(false);
          toast.success(`Sync complete! ${last_sync_count || 0} staff members synced.`);
          fetchSettings();
        } else if (status === "error") {
          clearInterval(interval);
          setSyncing(false);
          toast.error(error || "Sync failed");
        }
      } catch {
        // Keep polling
      }
    }, 5000);
    // Safety: stop polling after 5 minutes
    setTimeout(() => { clearInterval(interval); setSyncing(false); }, 300000);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    const success = await changePassword(passwordForm.current_password, passwordForm.new_password);
    
    if (success) {
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    }
    setChangingPassword(false);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Enter an email address");
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/notifications/test-email`, 
        { email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleTriggerEmailNotifications = async () => {
    setTriggeringEmails(true);
    try {
      const response = await axios.post(`${API}/notifications/send-emails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { emails_sent, sms_sent, skipped, failed } = response.data;
      toast.success(`Emails: ${emails_sent} | SMS: ${sms_sent} | Skipped: ${skipped} | Failed: ${failed}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger notifications");
    } finally {
      setTriggeringEmails(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhone) {
      toast.error("Enter a phone number");
      return;
    }
    setSendingTestSms(true);
    try {
      const response = await axios.post(`${API}/notifications/test-sms`,
        { phone: testPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test SMS");
    } finally {
      setSendingTestSms(false);
    }
  };

  const handleAssignFile = async (fileId) => {
    const staffId = selectedStaff[fileId];
    const certType = selectedCertType[fileId];
    if (!staffId) {
      toast.error("Please select a staff member");
      return;
    }
    if (!certType) {
      toast.error("Please select a certification type");
      return;
    }

    setAssigningFile(fileId);
    try {
      const payload = {
        staff_id: staffId,
        cert_type_id: certType,
      };
      // Include user-entered dates if provided
      const dates = manualDates[fileId] || {};
      if (dates.date_completed) {
        payload.date_completed = new Date(dates.date_completed + "T00:00:00Z").toISOString();
      }
      if (dates.expiration_date) {
        payload.expiration_date = new Date(dates.expiration_date + "T00:00:00Z").toISOString();
      }
      const response = await axios.post(`${API}/unmatched-files/${fileId}/assign`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.skipped) {
        toast.info(response.data.message);
      } else {
        const expMsg = response.data.expiration_date 
          ? ` (expires ${new Date(response.data.expiration_date).toLocaleDateString()})` 
          : "";
        toast.success(response.data.message + expMsg);
      }
      setUnmatchedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign file");
    } finally {
      setAssigningFile(null);
    }
  };

  const handleDeleteUnmatched = async (fileId) => {
    setDeletingFile(fileId);
    try {
      await axios.delete(`${API}/unmatched-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("File removed from queue");
      setUnmatchedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error("Failed to delete file");
    } finally {
      setDeletingFile(null);
    }
  };

  const handleMarkNotHired = async (fileId) => {
    setMovingToNotHired(fileId);
    try {
      await axios.put(`${API}/unmatched-files/${fileId}/not-hired`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const movedFile = unmatchedFiles.find(f => f.id === fileId);
      setUnmatchedFiles(prev => prev.filter(f => f.id !== fileId));
      if (movedFile) {
        setNotHiredFiles(prev => [{ ...movedFile, status: "not_hired" }, ...prev]);
      }
      toast.success("Moved to Not Hired");
    } catch {
      toast.error("Failed to move file");
    } finally {
      setMovingToNotHired(null);
    }
  };

  const handleRestoreFile = async (fileId) => {
    setRestoringFile(fileId);
    try {
      await axios.put(`${API}/unmatched-files/${fileId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const restoredFile = notHiredFiles.find(f => f.id === fileId);
      setNotHiredFiles(prev => prev.filter(f => f.id !== fileId));
      if (restoredFile) {
        setUnmatchedFiles(prev => [{ ...restoredFile, status: "pending" }, ...prev]);
      }
      toast.success("Restored to unmatched queue");
    } catch {
      toast.error("Failed to restore file");
    } finally {
      setRestoringFile(null);
    }
  };

  const fetchNotHiredFiles = async () => {
    setLoadingNotHired(true);
    try {
      const response = await axios.get(`${API}/unmatched-files?status=not_hired`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotHiredFiles(response.data);
    } catch {
      // Silently handle
    } finally {
      setLoadingNotHired(false);
    }
  };

  const handleViewUnmatched = async (fileId) => {
    try {
      const response = await axios.get(`${API}/unmatched-files/${fileId}/view`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to view file");
    }
  };

  const loadThumbnail = async (fileId) => {
    if (thumbnails[fileId] || loadingThumbnails[fileId]) return;
    setLoadingThumbnails(prev => ({ ...prev, [fileId]: true }));
    try {
      const response = await axios.get(`${API}/unmatched-files/${fileId}/thumbnail`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = URL.createObjectURL(response.data);
      setThumbnails(prev => ({ ...prev, [fileId]: url }));
    } catch {
      setThumbnails(prev => ({ ...prev, [fileId]: "error" }));
    } finally {
      setLoadingThumbnails(prev => ({ ...prev, [fileId]: false }));
    }
  };

  // Load thumbnails for visible unmatched files
  useEffect(() => {
    const start = unmatchedPage * ITEMS_PER_PAGE;
    const visible = unmatchedFiles.slice(start, start + ITEMS_PER_PAGE);
    visible.forEach(f => {
      if (!thumbnails[f.id] && !loadingThumbnails[f.id]) {
        loadThumbnail(f.id);
      }
    });
  }, [unmatchedFiles, unmatchedPage]);

  const handleBulkDeleteGarbled = async () => {
    // Delete unmatched files with very short or garbled OCR text (back-of-card pages)
    const garbled = unmatchedFiles.filter(f => {
      const text = (f.ocr_text || "").trim();
      return text.length < 30 || !text.match(/[a-zA-Z]{3,}\s+[a-zA-Z]{3,}/);
    });
    if (!garbled.length) {
      toast.info("No garbled/blank pages found to delete");
      return;
    }
    if (!window.confirm(`Delete ${garbled.length} pages that appear to be blank or back-of-card scans?`)) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    for (const f of garbled) {
      try {
        await axios.delete(`${API}/unmatched-files/${f.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch {}
    }
    toast.success(`Removed ${deleted} garbled/blank pages`);
    setBulkDeleting(false);
    fetchUnmatchedFiles();
  };

  const getFilteredStaff = (fileId) => {
    const search = (staffSearch[fileId] || "").toLowerCase();
    if (!search) return allStaff;
    return allStaff.filter(s => 
      (s.name || "").toLowerCase().includes(search) ||
      (s.first_name || "").toLowerCase().includes(search) ||
      (s.last_name || "").toLowerCase().includes(search)
    );
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    try {
      return new Date(isoStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return isoStr; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Configure integrations and account settings
        </p>
      </div>

      {/* Unmatched Files Queue */}
      {unmatchedFiles.length > 0 && (
        <div className="bg-white border-2 border-amber-300 rounded-lg overflow-hidden" data-testid="unmatched-files-section">
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FileQuestion className="h-5 w-5 text-amber-600" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-slate-900">
                    Unmatched Files ({unmatchedFiles.length})
                  </h2>
                  <p className="text-sm text-amber-700">
                    Scanned pages that couldn't be matched. View the image, then assign or delete.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleBulkDeleteGarbled}
                disabled={bulkDeleting}
                data-testid="bulk-delete-garbled-btn"
              >
                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                Delete Blank/Garbled Pages
              </Button>
            </div>
          </div>

          <div className="divide-y divide-slate-100 overflow-y-auto">
            {unmatchedFiles
              .slice(unmatchedPage * ITEMS_PER_PAGE, (unmatchedPage + 1) * ITEMS_PER_PAGE)
              .map((file) => {
              const filteredStaff = getFilteredStaff(file.id);
              const isDropdownOpen = staffDropdownOpen[file.id];

              return (
                <div key={file.id} className="px-6 py-4" data-testid={`unmatched-file-${file.id}`}>
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Left: Thumbnail + File info */}
                    <div className="flex gap-4 flex-1 min-w-0">
                      {/* Thumbnail with hover zoom */}
                      <div
                        className="relative flex-shrink-0 w-40 h-52"
                        onMouseEnter={() => setHoveredFileId(file.id)}
                        onMouseLeave={() => setHoveredFileId(null)}
                      >
                        <div className="w-full h-full bg-slate-100 rounded-lg border border-slate-200 overflow-hidden">
                        {thumbnails[file.id] && thumbnails[file.id] !== "error" ? (
                          <>
                            <img
                              src={thumbnails[file.id]}
                              alt="Cert preview"
                              className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => handleViewUnmatched(file.id)}
                              data-testid={`thumbnail-${file.id}`}
                            />
                          </>
                        ) : loadingThumbnails[file.id] ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
                          </div>
                        ) : (
                          <button
                            className="w-full h-full flex flex-col items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                            onClick={() => loadThumbnail(file.id)}
                          >
                            <Eye className="h-6 w-6 mb-1" />
                            <span className="text-xs">Load Preview</span>
                          </button>
                        )}
                        </div>
                        {/* Large hover preview */}
                        {hoveredFileId === file.id && thumbnails[file.id] && thumbnails[file.id] !== "error" && (
                          <div className="absolute left-full top-0 ml-3 z-50">
                            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3 ring-1 ring-black/5" style={{ width: "550px" }}>
                              <img
                                src={thumbnails[file.id]}
                                alt="Enlarged preview"
                                className="w-full object-contain rounded-lg transition-transform duration-200"
                                style={{ maxHeight: "700px", transform: `rotate(${imageRotation[file.id] || 0}deg)` }}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setImageRotation(prev => ({
                                    ...prev,
                                    [file.id]: ((prev[file.id] || 0) + 180) % 360
                                  }));
                                }}
                                className="absolute top-4 right-4 bg-white hover:bg-slate-100 border border-slate-300 rounded-full p-2 shadow-lg transition-colors cursor-pointer"
                                title="Flip 180°"
                                data-testid={`rotate-btn-${file.id}`}
                              >
                                <RotateCw className="h-5 w-5 text-slate-700" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                            {file.original_filename} {file.page_index != null ? `(pg ${file.page_index + 1})` : ""}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-blue-600 hover:text-blue-700"
                            onClick={() => handleViewUnmatched(file.id)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Full View
                          </Button>
                        </div>

                        {/* Detected dates */}
                        <div className="flex gap-4 text-xs text-slate-500 mb-2">
                          {file.date_completed && (
                            <span>Completed: <strong className="text-slate-700">{formatDate(file.date_completed)}</strong></span>
                          )}
                          {file.expiration_date && (
                            <span>Expires: <strong className="text-slate-700">{formatDate(file.expiration_date)}</strong></span>
                          )}
                        </div>

                        {/* OCR Text Preview (collapsible) */}
                        {file.ocr_text && (
                          <details className="mt-1">
                            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                              OCR text
                            </summary>
                            <div className="bg-slate-50 rounded p-2 mt-1 max-h-16 overflow-y-auto">
                              <p className="text-xs text-slate-500 whitespace-pre-wrap leading-relaxed">
                                {file.ocr_text.substring(0, 200)}
                              </p>
                            </div>
                          </details>
                        )}
                      </div>
                    </div>

                    {/* Right: Assignment controls */}
                    <div className="flex flex-col gap-2 lg:w-80 flex-shrink-0">
                      {/* Staff search dropdown */}
                      <div className="relative">
                        <Label className="text-xs text-slate-500 mb-1 block">Assign to Staff</Label>
                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                          <Input
                            placeholder="Search staff..."
                            className="pl-8 h-9 text-sm"
                            value={staffSearch[file.id] || ""}
                            onChange={(e) => {
                              setStaffSearch(prev => ({ ...prev, [file.id]: e.target.value }));
                              setStaffDropdownOpen(prev => ({ ...prev, [file.id]: true }));
                            }}
                            onFocus={() => setStaffDropdownOpen(prev => ({ ...prev, [file.id]: true }))}
                            data-testid={`staff-search-${file.id}`}
                          />
                          {selectedStaff[file.id] && (
                            <span className="absolute right-2.5 top-2 text-xs text-emerald-600 font-medium">
                              {allStaff.find(s => s.id === selectedStaff[file.id])?.name || "Selected"}
                            </span>
                          )}
                        </div>
                        {isDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredStaff.length === 0 ? (
                              <p className="px-3 py-2 text-xs text-slate-400">No staff found</p>
                            ) : (
                              filteredStaff.slice(0, 50).map(s => (
                                <button
                                  key={s.id}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between ${
                                    selectedStaff[file.id] === s.id ? "bg-emerald-50 text-emerald-700" : "text-slate-700"
                                  }`}
                                  onClick={() => {
                                    setSelectedStaff(prev => ({ ...prev, [file.id]: s.id }));
                                    setStaffSearch(prev => ({ ...prev, [file.id]: s.name }));
                                    setStaffDropdownOpen(prev => ({ ...prev, [file.id]: false }));
                                  }}
                                  data-testid={`staff-option-${s.id}`}
                                >
                                  <span>{s.last_name}, {s.first_name}</span>
                                  {!s.is_active && <span className="text-xs text-amber-500">Inactive</span>}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>

                      {/* Cert type dropdown */}
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Certification Type</Label>
                        <Select
                          value={selectedCertType[file.id] || file.detected_cert_type || "cpr_aed"}
                          onValueChange={(val) => setSelectedCertType(prev => ({ ...prev, [file.id]: val }))}
                        >
                          <SelectTrigger className="h-9 text-sm" data-testid={`cert-type-select-${file.id}`}>
                            <Shield className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CERT_TYPES.map(ct => (
                              <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date inputs */}
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500 mb-1 block">Completed</Label>
                          <Input
                            type="date"
                            className="h-9 text-sm"
                            value={(manualDates[file.id] || {}).date_completed || ""}
                            onChange={(e) => setManualDates(prev => ({
                              ...prev,
                              [file.id]: { ...(prev[file.id] || {}), date_completed: e.target.value }
                            }))}
                            data-testid={`date-completed-${file.id}`}
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500 mb-1 block">Expires</Label>
                          <Input
                            type="date"
                            className="h-9 text-sm"
                            value={(manualDates[file.id] || {}).expiration_date || ""}
                            onChange={(e) => setManualDates(prev => ({
                              ...prev,
                              [file.id]: { ...(prev[file.id] || {}), expiration_date: e.target.value }
                            }))}
                            data-testid={`expiration-date-${file.id}`}
                          />
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 mt-1">
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                          onClick={() => handleAssignFile(file.id)}
                          disabled={!selectedStaff[file.id] || assigningFile === file.id}
                          data-testid={`assign-btn-${file.id}`}
                        >
                          {assigningFile === file.id ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Assign
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-200 text-amber-700 hover:bg-amber-50 h-9"
                          onClick={() => handleMarkNotHired(file.id)}
                          disabled={movingToNotHired === file.id}
                          data-testid={`not-hired-btn-${file.id}`}
                          title="Move to Not Hired holding area"
                        >
                          {movingToNotHired === file.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <UserX className="h-3.5 w-3.5" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50 h-9"
                          onClick={() => handleDeleteUnmatched(file.id)}
                          disabled={deletingFile === file.id}
                          data-testid={`delete-btn-${file.id}`}
                        >
                          {deletingFile === file.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {unmatchedFiles.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-t border-slate-200" data-testid="unmatched-pagination">
              <span className="text-sm text-slate-500">
                Showing {unmatchedPage * ITEMS_PER_PAGE + 1}–{Math.min((unmatchedPage + 1) * ITEMS_PER_PAGE, unmatchedFiles.length)} of {unmatchedFiles.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unmatchedPage === 0}
                  onClick={() => setUnmatchedPage(p => p - 1)}
                  data-testid="unmatched-prev-btn"
                >
                  Previous
                </Button>
                <span className="text-sm font-medium text-slate-700 px-2">
                  {unmatchedPage + 1} / {Math.ceil(unmatchedFiles.length / ITEMS_PER_PAGE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(unmatchedPage + 1) * ITEMS_PER_PAGE >= unmatchedFiles.length}
                  onClick={() => setUnmatchedPage(p => p + 1)}
                  data-testid="unmatched-next-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Not Hired Holding Area */}
      <div className="bg-white border border-amber-200 rounded-lg" data-testid="not-hired-section">
        <button
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-amber-50/50 transition-colors"
          onClick={() => {
            setShowNotHired(!showNotHired);
            if (!showNotHired && notHiredFiles.length === 0) {
              fetchNotHiredFiles();
            }
          }}
          data-testid="not-hired-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <UserX className="h-5 w-5 text-amber-700" />
            </div>
            <div className="text-left">
              <h2 className="font-heading font-semibold text-slate-900">
                Not Hired ({notHiredFiles.length})
              </h2>
              <p className="text-sm text-slate-500">Certs for people not yet in the system</p>
            </div>
          </div>
          <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${showNotHired ? "rotate-180" : ""}`} />
        </button>

        {showNotHired && (
          <div className="border-t border-amber-200">
            {loadingNotHired ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 text-amber-500 animate-spin" />
              </div>
            ) : notHiredFiles.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                No files in the Not Hired area
              </div>
            ) : (
              <div className="divide-y divide-amber-100">
                {notHiredFiles.map(file => (
                  <div key={file.id} className="px-6 py-3 flex items-center gap-4" data-testid={`not-hired-file-${file.id}`}>
                    {/* Mini thumbnail */}
                    <div className="relative group flex-shrink-0 w-16 h-20">
                      <div className="w-full h-full bg-slate-100 rounded border border-slate-200 overflow-hidden">
                        {thumbnails[file.id] && thumbnails[file.id] !== "error" ? (
                          <img
                            src={thumbnails[file.id]}
                            alt="Preview"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <button
                            className="w-full h-full flex items-center justify-center text-slate-400 hover:text-slate-600"
                            onClick={() => loadThumbnail(file.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {/* Hover preview for not-hired items */}
                      {thumbnails[file.id] && thumbnails[file.id] !== "error" && (
                        <div className="hidden group-hover:block absolute left-full top-0 ml-2 z-50">
                          <div className="w-64 bg-white rounded-lg shadow-2xl border border-slate-200 p-1.5 ring-1 ring-black/5">
                            <img
                              src={thumbnails[file.id]}
                              alt="Enlarged preview"
                              className="w-full object-contain rounded"
                              style={{ maxHeight: "360px" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-mono text-slate-500 truncate block">
                        {file.original_filename} {file.page_index != null ? `(pg ${file.page_index + 1})` : ""}
                      </span>
                      {file.detected_cert_type && (
                        <span className="text-xs text-amber-600">Detected: {file.detected_cert_type}</span>
                      )}
                    </div>

                    {/* Restore button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8"
                      onClick={() => handleRestoreFile(file.id)}
                      disabled={restoringFile === file.id}
                      data-testid={`restore-btn-${file.id}`}
                    >
                      {restoringFile === file.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Camp Season Dates */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="camp-dates-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-brand-navy/10 flex items-center justify-center">
            <Calendar className="h-5 w-5 text-brand-navy" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Camp Season Dates</h2>
            <p className="text-sm text-slate-500">
              Used on the Reports page to flag certs expiring mid-camp. Affects color coding only.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="camp_start">Camp Start Date</Label>
            <Input
              id="camp_start"
              type="date"
              value={campStart}
              onChange={(e) => setCampStart(e.target.value)}
              data-testid="camp-start-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="camp_end">Camp End Date</Label>
            <Input
              id="camp_end"
              type="date"
              value={campEnd}
              onChange={(e) => setCampEnd(e.target.value)}
              data-testid="camp-end-input"
            />
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={handleSaveCampDates}
            disabled={savingCampDates}
            className="bg-brand-navy hover:bg-brand-navy/90"
            data-testid="save-camp-dates-button"
          >
            {savingCampDates ? "Saving..." : "Save Camp Dates"}
          </Button>
        </div>
      </div>

      {/* Backfill Cross-Cert Links */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="backfill-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-brand-navy/10 flex items-center justify-center">
            <Wrench className="h-5 w-5 text-brand-navy" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Backfill Cert Cross-Links</h2>
            <p className="text-sm text-slate-500">
              Apply combo→CPR and CEU→CPR auto-extensions to existing staff. Idempotent — safe to re-run.
            </p>
          </div>
        </div>

        {!backfillPreview && (
          <Button
            onClick={handleRunBackfillPreview}
            disabled={runningBackfill}
            variant="outline"
            data-testid="preview-backfill-button"
          >
            {runningBackfill ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</>
            ) : (
              "Preview Changes"
            )}
          </Button>
        )}

        {backfillPreview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <p className="text-xs text-slate-500">Staff scanned</p>
                <p className="text-2xl font-semibold text-slate-900">{backfillPreview.total_staff_scanned}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-xs text-emerald-700">Combo → CPR</p>
                <p className="text-2xl font-semibold text-emerald-900">{backfillPreview.combo_extended_cpr}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-xs text-emerald-700">CEU → CPR</p>
                <p className="text-2xl font-semibold text-emerald-900">{backfillPreview.ceu_extended_cpr}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-xs text-amber-700">Missing dates</p>
                <p className="text-2xl font-semibold text-amber-900">{backfillPreview.staff_with_missing_dates}</p>
              </div>
            </div>

            {backfillPreview.details && backfillPreview.details.length > 0 && (
              <div className="border border-slate-200 rounded-md max-h-72 overflow-y-auto" data-testid="backfill-details-list">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-semibold">Staff</th>
                      <th className="text-left p-2 font-semibold">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backfillPreview.details.slice(0, 100).map((d) => (
                      <tr key={d.staff_id} className="border-t border-slate-100">
                        <td className="p-2 font-medium text-slate-900 whitespace-nowrap">{d.name}</td>
                        <td className="p-2 text-slate-600">
                          {d.changes.map((c, i) => (
                            <div key={i}>{c}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {backfillPreview.details.length > 100 && (
                  <p className="text-xs text-slate-500 p-2 bg-slate-50">
                    + {backfillPreview.details.length - 100} more affected staff
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleApplyBackfill}
                disabled={applyingBackfill || (backfillPreview.combo_extended_cpr === 0 && backfillPreview.ceu_extended_cpr === 0)}
                className="bg-brand-navy hover:bg-brand-navy/90"
                data-testid="apply-backfill-button"
              >
                {applyingBackfill ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</>
                ) : (
                  `Apply ${backfillPreview.combo_extended_cpr + backfillPreview.ceu_extended_cpr} Changes`
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setBackfillPreview(null)}
                disabled={applyingBackfill}
                data-testid="cancel-backfill-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Auto-extract dates from cert files via OCR + Gemini Vision — now AUTOMATIC */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-1">Auto-Extract Missing Cert Dates</h3>
          <p className="text-sm text-slate-500 mb-3">
            Runs automatically on every CampMinder sync and on backend startup. Reads expiration dates directly from uploaded cert files using OCR + Gemini Vision. No re-upload, no buttons.
          </p>

          {extractJob && extractJob.status === "running" && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-3" data-testid="extract-job-progress">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Processing certificates…</span>
              </div>
              <p className="text-xs text-blue-700">
                {extractJob.progress?.processed || 0} / {extractJob.progress?.total || "?"} staff scanned
                {extractJob.progress?.dates_extracted_so_far !== undefined && (
                  <> • {extractJob.progress.dates_extracted_so_far} dates extracted</>
                )}
              </p>
              <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{
                    width: extractJob.progress?.total
                      ? `${((extractJob.progress.processed || 0) / extractJob.progress.total) * 100}%`
                      : "0%"
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleAutoExtractDates}
              disabled={pollingJob}
              variant="outline"
              size="sm"
              data-testid="auto-extract-button"
            >
              {pollingJob ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
              ) : (
                <>Re-run Now</>
              )}
            </Button>
            <span className="text-xs text-slate-500">(optional — auto-runs after each sync)</span>
          </div>

          {extractJob?.status === "complete" && extractJob.summary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mt-3" data-testid="extract-job-result">
              <p className="text-sm font-semibold text-emerald-900 mb-2">Last run complete</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-emerald-700">Dates extracted</p>
                  <p className="text-xl font-semibold text-emerald-900">{extractJob.summary.dates_auto_extracted}</p>
                </div>
                <div>
                  <p className="text-amber-700">Low confidence</p>
                  <p className="text-xl font-semibold text-amber-900">{extractJob.summary.low_confidence_extractions}</p>
                </div>
                <div>
                  <p className="text-red-700">Need manual review</p>
                  <p className="text-xl font-semibold text-red-900">{extractJob.summary.needs_manual_review}</p>
                </div>
                <div>
                  <p className="text-slate-700">Still missing</p>
                  <p className="text-xl font-semibold text-slate-900">{extractJob.summary.missing_dates_flagged}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CampMinder Settings */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="campminder-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-brand-navy/10 flex items-center justify-center">
            <Key className="h-5 w-5 text-brand-navy" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">CampMinder Integration</h2>
            <p className="text-sm text-slate-500">Connect to sync staff data automatically</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subscription_key">Subscription Key (Ocp-Apim-Subscription-Key)</Label>
            <Input
              id="subscription_key"
              type="password"
              placeholder="Enter your CampMinder subscription key"
              value={settings.campminder_subscription_key}
              onChange={(e) => setSettings({ ...settings, campminder_subscription_key: e.target.value })}
              data-testid="campminder-key-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Button
              variant="outline"
              onClick={testCampMinderConnection}
              disabled={testingConnection}
              data-testid="test-campminder-connection"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${testingConnection ? "animate-spin" : ""}`} />
              Test Connection
            </Button>
            
            {connectionStatus && (
              <div className={`flex items-center gap-2 text-sm ${
                connectionStatus.connected ? "text-emerald-600" : "text-red-600"
              }`}>
                {connectionStatus.connected ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Connected
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    {connectionStatus.error}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Season Sync Section */}
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Sync Staff from CampMinder</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="space-y-1">
                <Label htmlFor="season_id" className="text-xs text-slate-500">Season Year</Label>
                <Select value={seasonId} onValueChange={setSeasonId}>
                  <SelectTrigger className="w-32" data-testid="season-select">
                    <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2023">2023</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSyncCampMinder}
                  disabled={syncing}
                  className="bg-brand-navy hover:bg-brand-navy/90"
                  data-testid="sync-campminder-button"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing..." : "Sync Staff"}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Fetches active staff from CampMinder for the selected season
            </p>
          </div>

          {/* Repair Categories — admin recovery tool */}
          <div className="pt-4 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Repair Staff Categories</h3>
            <p className="text-xs text-slate-500 mb-3">
              Recompute every active staff member's category from their current division + position. Use this if any division section disappears from the Staff List or Reports.
            </p>
            <Button
              onClick={handleRepairCategories}
              disabled={repairing}
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="repair-categories-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${repairing ? "animate-spin" : ""}`} />
              {repairing ? "Repairing..." : "Repair Staff Categories"}
            </Button>
          </div>
        </div>
      </div>

      {/* Email & SMS Notifications */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="email-notification-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Bell className="h-5 w-5 text-indigo-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Email & SMS Notifications</h2>
            <p className="text-sm text-slate-500">Send expiration reminders to staff via email and text</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-sm text-indigo-800 font-medium mb-1">Notification Schedule</p>
            <p className="text-xs text-indigo-600">
              Emails & texts sent automatically at: <strong>2 months</strong>, <strong>1 month</strong>, <strong>6 days</strong>, and <strong>on expiration day</strong>
            </p>
            <p className="text-xs text-indigo-500 mt-1">
              Daily check runs at 8:00 AM EST. Duplicates prevented per staff/cert/day/channel.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleTriggerEmailNotifications}
              disabled={triggeringEmails}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="trigger-email-notifications-btn"
            >
              {triggeringEmails ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Send All Notifications Now
            </Button>
          </div>

          <div className="border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Test Email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your-email@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                  data-testid="test-email-input"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail || !testEmail}
                  data-testid="send-test-email-btn"
                >
                  {sendingTestEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Test SMS</Label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="(516) 555-1234"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="flex-1"
                  data-testid="test-sms-input"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTestSms}
                  disabled={sendingTestSms || !testPhone}
                  data-testid="send-test-sms-btn"
                >
                  {sendingTestSms ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                </Button>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Sending from: (516) 350-5091. Staff will receive both email and text reminders.
          </p>
        </div>
      </div>

      {/* Google Drive Settings */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="google-drive-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <FolderOpen className="h-5 w-5 text-blue-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Google Drive</h2>
            <p className="text-sm text-slate-500">Storage for certification files</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="drive_folder">Folder ID</Label>
            <Input
              id="drive_folder"
              placeholder="1vC76bVS8ZKnhJm8L_neyAODmNCtANr09"
              value={settings.google_drive_folder_id}
              onChange={(e) => setSettings({ ...settings, google_drive_folder_id: e.target.value })}
              data-testid="google-drive-folder-input"
            />
            <p className="text-xs text-slate-500">
              The folder ID from your Google Drive URL
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSaveSettings}
        disabled={saving}
        className="bg-brand-navy hover:bg-brand-navy/90 btn-lift"
        data-testid="save-settings-button"
      >
        <Save className="h-4 w-4 mr-2" />
        {saving ? "Saving..." : "Save Settings"}
      </Button>


      {/* Notification Log */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="notification-log-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Notification Log</h2>
          <Button
            onClick={refreshDeliveryStatus}
            disabled={refreshingStatus}
            variant="outline"
            size="sm"
            data-testid="refresh-delivery-status-btn"
          >
            {refreshingStatus ? "Refreshing..." : "Refresh Delivery Status"}
          </Button>
        </div>
        
        {loadingLogs ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : notificationLogs.length === 0 ? (
          <p className="text-sm text-slate-500">No notifications sent yet</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-semibold">Date</th>
                  <th className="text-left p-2 font-semibold">Staff</th>
                  <th className="text-left p-2 font-semibold">Cert</th>
                  <th className="text-left p-2 font-semibold">Type</th>
                  <th className="text-left p-2 font-semibold">Channel</th>
                  <th className="text-left p-2 font-semibold">To</th>
                  <th className="text-left p-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {notificationLogs.map((log, i) => (
                  <tr key={log.id || i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`notification-log-row-${i}`}>
                    <td className="p-2 whitespace-nowrap">{log.sent_at ? new Date(log.sent_at).toLocaleString() : log.sent_date}</td>
                    <td className="p-2">{log.staff_name}</td>
                    <td className="p-2">{log.cert_name}</td>
                    <td className="p-2 capitalize">{log.notification_type?.replace("_", " ")}</td>
                    <td className="p-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.channel === "sms" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                      }`}>
                        {log.channel === "sms" ? "SMS" : "Email"}
                      </span>
                    </td>
                    <td className="p-2 text-xs text-slate-500">{log.channel === "sms" ? log.staff_phone : log.staff_email}</td>
                    <td className="p-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.delivery_status === "delivered" ? "bg-green-100 text-green-700" :
                        log.delivery_status === "failed" || log.delivery_status === "undelivered" ? "bg-red-100 text-red-700" :
                        log.delivery_status === "sent" ? "bg-emerald-100 text-emerald-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {log.delivery_status || "sent"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Password Change */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="password-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
            <Lock className="h-5 w-5 text-red-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Change Password</h2>
            <p className="text-sm text-slate-500">Update your admin password</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              data-testid="current-password-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <Input
              id="new_password"
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              data-testid="new-password-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <Input
              id="confirm_password"
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password}
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50"
            data-testid="change-password-button"
          >
            <Lock className="h-4 w-4 mr-2" />
            {changingPassword ? "Changing..." : "Change Password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
